/* global axios */
import ApiClient from './ApiClient';
import { resourcePath, requestConfig, withQuery } from './lais/httpHelpers';
import { OPEN_HOUSE_ENDPOINTS } from './lais/endpoints';

/**
 * OpenHouseEventsAPI — authenticated client for the B09 open-house aggregate.
 *
 * Every mutation is a member command on the event so the backend's capacity /
 * waitlist / check-in invariants stay server-side. Registration-scoped commands
 * (confirm, cancel_registration, convert) carry `registration_id` in the body,
 * matching `OpenHouseEventsController`.
 */
class OpenHouseEventsAPI extends ApiClient {
  constructor() {
    super('open_house_events', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(withQuery(this.url, params), requestConfig());
  }

  show(id) {
    return axios.get(resourcePath(this.url, id), requestConfig());
  }

  create(payload = {}) {
    const { hosts, ...event } = payload;
    const body = { open_house_event: event };
    if (hosts) body.hosts = hosts;
    return axios.post(this.url, body, requestConfig());
  }

  update(id, payload = {}) {
    const { hosts, ...event } = payload;
    const body = { open_house_event: event };
    if (hosts) body.hosts = hosts;
    return axios.patch(resourcePath(this.url, id), body, requestConfig());
  }

  metrics(id) {
    return axios.get(
      resourcePath(this.url, id, OPEN_HOUSE_ENDPOINTS.metrics),
      requestConfig()
    );
  }

  // --- Event lifecycle -------------------------------------------------------

  schedule(id) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.schedule);
  }

  open(id) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.open);
  }

  close(id) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.close);
  }

  cancel(id, payload = {}) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.cancel, payload);
  }

  // --- Registrations ---------------------------------------------------------

  register(id, payload = {}) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.register, payload);
  }

  confirmRegistration(id, registrationId) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.confirm, {
      registration_id: registrationId,
    });
  }

  cancelRegistration(id, registrationId, payload = {}) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.cancelRegistration, {
      registration_id: registrationId,
      ...payload,
    });
  }

  promote(id) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.promote);
  }

  noShowRegistration(id, registrationId) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.noShow, {
      registration_id: registrationId,
    });
  }

  // --- Check-in --------------------------------------------------------------

  checkIn(id, payload = {}) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.checkIn, payload);
  }

  walkIn(id, payload = {}) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.walkIn, payload);
  }

  // --- Conversion ------------------------------------------------------------

  convert(id, payload = {}) {
    return this.command(id, OPEN_HOUSE_ENDPOINTS.convert, payload);
  }

  command(id, action, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, action),
      payload,
      requestConfig()
    );
  }
}

export default new OpenHouseEventsAPI();
